/**
 * Author: Robert Banziziki Muhire
 * Date: 18/06/2020
 */

import { combineLatest } from "rxjs";
import { map, filter } from "rxjs/operators";
import { GameActionEntity } from "./interfaces/Action.interface";
import { ChallengeEntity } from "./interfaces/Challenge.interface";
import { ChapterEntity } from "./interfaces/Chapter.interface";
import { QuizzEntity } from "./interfaces/Quizz.interface";
import { stringify as queryBuilder } from "qs";
import { ApiEntity } from "../Api/api.model";
import { LevelEntity } from "./interfaces/Level.interface";
import { AchievementEntity } from "./interfaces/Achievement.interface";
import { RewardEntity } from "./interfaces/Reward.interface";
import { LockerEntity } from "./interfaces/Locker.interface";
import { GamificationEntity } from "./interfaces/Gamification.interface";
import { TutorialEntity } from "./interfaces/Tutorial.interface";

const TARGETS: Record<string, ApiEntity<any>> = {
  chapters: new ChapterEntity() as unknown as ApiEntity<any>,
  challenges: new ChallengeEntity() as unknown as ApiEntity<any>,
};

function gameDataObservable(locale: string = null) {
  const chapters$ = new ChapterEntity().getAll({ locale });
  const challenges$ = new ChallengeEntity().getAll({ locale });
  const action$ = new GameActionEntity().getAll({ locale });
  return combineLatest([chapters$, challenges$, action$]).pipe(
    map(([chapters, challenges, actions]) => ({
      chapters,
      challenges,
      actions,
    }))
  );
}

function quizzesObservable(locale: string = null) {
  return new QuizzEntity().getAll({ locale });
}

function quizzByNameObservable(value: string, locale: string) {
  return new QuizzEntity().getByName(value, { locale });
}

function quizzByIdObservable(id: string) {
  return new QuizzEntity().get(id);
}

function chapterByIdObservable(id: string) {
  return new ChapterEntity().get(id);
}

function chapterByListIdsObservable(ids: Array<string>, locale: string) {
  return new ChallengeEntity().getByListParam("id", ids, { locale });
}

function challengesByListIdsObservable(ids: Array<string>, locale: string) {
  return new ChallengeEntity().getByListParam("id", ids, { locale });
}

function chaptersObservable(locale: string) {
  return new ChapterEntity().getAll({ locale });
}

function actionsObservable(locale: string) {
  return new GameActionEntity().getAll({ locale });
}

function challengesObservable(locale: string) {
  return new ChallengeEntity().getAll({ locale });
}

function actionsByListIdsObservable(ids: Array<string>, locale: string) {
  return new GameActionEntity().getByListParam("id", ids, { locale });
}

function challengesByCategoryObservable(category: string, locale: string) {
  return new ChallengeEntity().getByListParam("category", [category], {
    locale,
  });
}

function challengeByIdObservable(id: string) {
  return new ChallengeEntity().get(id).pipe(
    filter((challenges) => !!challenges[0]),
    map((challenges) => challenges[0])
  );
}

function targetByIndexObservable(
  index: string,
  target: string,
  locale: string
) {
  const request = queryBuilder({
    _where: [{ _locale: locale }, { index_eq: index }],
  });
  return TARGETS[target].customQuery(request);
}

function levelByNameObservable(name: string, locale: string) {
  return new LevelEntity().getByName(name, { locale }).pipe(
    filter((levels) => levels.length > 0),
    map((levels) => levels[0])
  );
}

function achievementsObservable(locale: string) {
  return new AchievementEntity().getAll({ locale });
}

function rewardsObservable(locale: string) {
  return new RewardEntity().getAll({ locale });
}

function lockersObservable() {
  return new LockerEntity().getAll();
}

function tutorialsObservable(locale: string) {
  return new TutorialEntity().getAll({ locale });
}

function gamificationObservable() {
  return new GamificationEntity().getAll().pipe(
    filter((g) => g.length > 0),
    map((g) => g[0])
  );
}

export {
  gameDataObservable,
  targetByIndexObservable,
  quizzesObservable,
  quizzByNameObservable,
  quizzByIdObservable,
  chapterByIdObservable,
  challengesByListIdsObservable,
  actionsByListIdsObservable,
  chaptersObservable,
  challengesByCategoryObservable,
  challengesObservable,
  actionsObservable,
  challengeByIdObservable,
  chapterByListIdsObservable,
  levelByNameObservable,
  achievementsObservable,
  rewardsObservable,
  lockersObservable,
  gamificationObservable,
  tutorialsObservable,
};
